import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";
import { VOICES, ASSETS } from "@/data/content";
import { Reveal } from "@/lib/reveal";

const FEATURED = VOICES.filter((v) => v.portrait && ASSETS[v.portrait]);

// Short lines from contributors whose portraits are still to come (derived, no repeats).
const CHORUS = VOICES.filter((v) => !v.portrait || !ASSETS[v.portrait]);

const Portrait = ({ v, className = "", thumb = false }) => {
  const wc = v.kind === "wc";
  const bg = wc ? "bg-gradient-to-b from-[#efe4cb] to-[#d3c095]" : "";
  const fit = wc ? "object-contain object-center" : "object-cover object-top";
  return (
    <div className={`${bg} ${className}`}>
      <img src={ASSETS[v.portrait]} alt={v.name} className={`h-full w-full ${fit} ${wc && !thumb ? "p-3" : ""}`} />
    </div>
  );
};

export const Voices = () => {
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);
  const [paused, setPaused] = useState(false);
  const v = FEATURED[i];

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => { setDir(1); setI((p) => (p + 1) % FEATURED.length); }, 5500);
    return () => clearInterval(t);
  }, [paused]);

  const go = (d) => { setDir(d); setI((p) => (p + d + FEATURED.length) % FEATURED.length); };

  return (
    <section id="voices" data-testid="voices" className="relative overflow-hidden bg-ink py-20 md:py-28">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <Reveal>
          <div className="flex items-end justify-between gap-6">
            <div>
              <span className="font-body text-xs uppercase tracking-[0.3em] text-ochre">Featured Voices</span>
              <h2 className="mt-3 font-display text-4xl font-medium text-cream sm:text-5xl lg:text-6xl">The people we sat down with.</h2>
            </div>
            <div className="hidden items-center gap-2 md:flex">
              <button onClick={() => go(-1)} data-testid="voices-prev" className="grid h-11 w-11 place-items-center rounded-full border border-cream/25 text-cream transition-colors hover:border-ochre hover:text-ochre"><ArrowLeft size={18} /></button>
              <button onClick={() => go(1)} data-testid="voices-next" className="grid h-11 w-11 place-items-center rounded-full border border-cream/25 text-cream transition-colors hover:border-ochre hover:text-ochre"><ArrowRight size={18} /></button>
            </div>
          </div>
        </Reveal>

        {/* Featured portrait + quote */}
        <div
          className="mt-10 grid items-center gap-8 lg:grid-cols-[0.82fr_1.18fr] lg:gap-14"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="voices-featured"
        >
          <div className="relative mx-auto aspect-[4/5] w-full max-w-sm overflow-hidden rounded-2xl border border-cream/10 shadow-[0_28px_60px_rgba(0,0,0,0.4)]">
            <AnimatePresence mode="wait">
              <motion.div
                key={v.name}
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.99 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0"
              >
                <Portrait v={v} className="h-full w-full" />
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="relative">
            <Quote className="text-ochre/40" size={48} />
            <AnimatePresence mode="wait">
              <motion.blockquote
                key={v.name}
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="mt-2"
              >
                <p className="font-display text-2xl font-medium leading-[1.2] text-cream md:text-4xl md:leading-[1.15]">{v.quote}</p>
                <footer className="mt-7 flex items-center gap-3">
                  <span className="h-px w-10 bg-ochre" />
                  <div>
                    <div className="font-display text-xl font-medium text-ochre">{v.name}</div>
                    <div className="max-w-lg font-body text-sm text-cream/70">{v.role}</div>
                  </div>
                </footer>
              </motion.blockquote>
            </AnimatePresence>
          </div>
        </div>

        {/* Filmstrip of all portrait contributors */}
        <div className="mt-10 flex flex-wrap gap-3" data-testid="voices-wall">
          {FEATURED.map((f, idx) => (
            <button
              key={f.name}
              data-testid={`voice-chip-${idx}`}
              onClick={() => { setDir(idx > i ? 1 : -1); setI(idx); }}
              title={f.name}
              className={`h-16 w-16 overflow-hidden rounded-full border-2 transition-all duration-300 ${i === idx ? "scale-105 border-ochre" : "border-cream/25 opacity-55 hover:opacity-100"}`}
            >
              <Portrait v={f} thumb className="h-full w-full" />
            </button>
          ))}
        </div>

        {/* Woven chorus, contributors whose portraits are still to come */}
        {CHORUS.length > 0 && (
          <div className="relative mt-8 overflow-hidden rounded-2xl bg-forest px-6 py-10 md:px-14 md:py-14">
            <img src={ASSETS.wcWalking} alt="" aria-hidden className="pointer-events-none absolute -bottom-6 right-0 hidden h-[130%] w-auto object-contain opacity-90 md:block" />
            <div className="relative max-w-3xl">
              <span className="font-body text-xs uppercase tracking-[0.3em] text-ochre">And a chorus of the committed</span>
              <p className="mt-5 font-display text-xl leading-relaxed text-cream/95 md:text-2xl md:leading-snug">
                {CHORUS.map((c, idx) => (
                  <span key={c.name}>
                    <span className="text-cream">“{c.quote}”</span>{" "}
                    <span className="font-script text-ochre text-[1.15em]">{c.name}</span>
                    {idx < CHORUS.length - 1 && <span className="text-cream/40">{"  ·  "}</span>}
                  </span>
                ))}
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
